import dayjs from 'dayjs'
import {
  unwrapFieldValue,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import type { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useGeckoNotifications } from 'features/notifications'
import { useCallback, useMemo } from 'react'
import { v4 } from 'uuid'
import { z } from 'zod'

export const ZONE_FIELD_IDS = {
  ZONES: 'zones',
  BREAKDOWN_JUSTIFICATION: 'zone_breakdown_justification',
} as const

const now = () => dayjs().format('YYYY-MM-DD')

// ============================================================================
// Shared Types
// ============================================================================

export type TriState = 'low' | 'medium' | 'high'

export enum InspectionStrategy {
  A = 'A',
  B = 'B',
  C = 'C',
}

// Consequence of Failure Schema (Global - applies to all zones)
export const consequenceOfFailureSchema = z.object({
  level: z.enum(['low', 'medium', 'high']).default('medium'),
  justification: z.string().optional(),
})

// Damage Mechanism Schema
export const damageMechanismSchema = z.object({
  id: z.string().default(() => v4().substring(0, 6)),
  name: z.string(),
  rate_value: z.number().optional(),
  rate_unit: z.string().optional().default('mm/y'),
  susceptibility: z.enum(['low', 'medium', 'high']).optional(),
  justification: z.string().optional(),
})

// NII Strategy Schema
export const niiStrategySchema = z.object({
  strategy_type_calculated: z.nativeEnum(InspectionStrategy).optional(),
  strategy_type_override: z.nativeEnum(InspectionStrategy).optional(),
  degradation_likely: z.boolean().optional(),
  degradation_likely_justification: z.string().optional(),
  degradation_predictable: z.boolean().optional(),
  degradation_predictable_justification: z.string().optional(),
  degradation_severity: z.enum(['low', 'medium', 'high']).optional(),
  degradation_severity_justification: z.string().optional(),
  // Top-level justification: auto-set to the last answered step's justification
  justification: z.string().optional(),
  // Confidence from NII Screening (read-only, used for coverage calculations)
  confidence: z.enum(['low', 'med', 'high']).optional(),
})

// RBA Factor Schema
export const rbaFactorSchema = z.object({
  value: z.enum(['low', 'medium', 'high']).default('medium'),
  modifier: z.number().default(1.0),
  reason: z.string().optional(),
  informs: z.array(z.string()).optional(),
})

// RBA Information Schema
export const rbaInformationSchema = z.object({
  // Type A & B: Density is used for F_COV lookup (not a standalone modifier)
  density_of_degradation: z.enum(['low', 'medium', 'high']).optional(),
  density_justification: z.string().optional(),

  // Type B only: Additional modifiers
  measurement_accuracy: z.enum(['low', 'medium', 'high']).optional(), // F_ACC
  measurement_accuracy_justification: z.string().optional(),
  tolerance_of_degradation: z.enum(['low', 'medium', 'high']).optional(), // F_LC
  tolerance_justification: z.string().optional(),
  spatial_homogeneity: z.enum(['low', 'medium', 'high']).optional(), // F_SH
  spatial_homogeneity_justification: z.string().optional(),
})

// Coverage Calculation Schema
export const coverageCalculationSchema = z.object({
  // Type A & B
  base_coverage_percent: z.number().optional(), // C_1 (7.5% or 25%)

  // Individual modifiers (for display/debugging)
  f_cov: z.number().optional(), // From Confidence × Density lookup
  f_acc: z.number().optional(), // Type B only: Measurement accuracy
  f_lc: z.number().optional(), // Type B only: Tolerance to degradation
  f_sh: z.number().optional(), // Type B only: Spatial homogeneity
  f_cons: z.number().optional(), // Consequence of failure
  f_zone: z.number().optional(), // Zone area modifier

  // Pre-clamp result (product of modifiers × base coverage, before min/max)
  raw_calculated_percent: z.number().optional(),

  // Final results (after clamping)
  required_coverage_percent: z.number().optional(),
  required_coverage_area_sq_m: z.number().optional(),
})

// Zone Parameters Schema
export const zoneParamsSchema = z.object({
  id: z.string().default(() => v4().substring(0, 6)),
  title: z.string().default('Untitled Zone'),
  description: z.string().optional(),
  material: z.string().optional(),
  geometry: z.string().optional(),
  calculated_length_mm: z.number().optional(),
  length_mm_override: z.number().optional(),
  calculated_height_mm: z.number().optional(),
  height_mm_override: z.number().optional(),
  length_mm: z.number().optional(),
  height_mm: z.number().optional(),
  internal_diameter_mm: z.number().optional(),
  external_diameter_mm: z.number().optional(),
  total_surface_area_sq_m: z.number().optional(),
  wall_thickness_shell_mm: z.number().optional(),
  wall_thickness_heads_mm: z.number().optional(),
  corrosion_allowance_mm: z.number().optional(),

  // Asset Model Reference (legacy/1% use case)
  componentId: z.string().optional(),
  bounds: z
    .object({
      x_start: z.number(),
      y_start: z.number(),
      x_end: z.number(),
      y_end: z.number(),
      unit: z.string(),
    })
    .optional(),

  // Zone Reference Image (primary/99% use case)
  zone_reference_image_id: z.string().optional(), // REPORT entry ID
  drawing_bounds: z
    .object({
      x_start: z.number(), // pixels
      y_start: z.number(),
      x_end: z.number(),
      y_end: z.number(),
    })
    .optional(),

  // AI suggestion review (POC): a zone proposed by the AI that the SME must
  // confirm / reject / edit. `ai_suggested` is true until confirmed.
  // `ai_initial_bounds` freezes the model's original box so it can be compared
  // against the SME-edited `drawing_bounds` (feedback signal).
  ai_suggested: z.boolean().optional(),
  ai_initial_bounds: z
    .object({
      x_start: z.number(), // pixels
      y_start: z.number(),
      x_end: z.number(),
      y_end: z.number(),
    })
    .optional(),
  ai_meta: z
    .object({
      model: z.string().optional(),
      generated_at: z.string().optional(),
    })
    .optional(),

  calculated_area_sq_m: z.number().optional(),
  total_surface_area_sq_m_override: z.number().optional(),
  color: z.string().optional(),
  inspection_method: z.string().optional(),
  damage_mechanisms: z.array(damageMechanismSchema).optional().default([]),
  nii_strategy: niiStrategySchema.optional(),
  rba_info: rbaInformationSchema.optional(),
  coverage_calculation: coverageCalculationSchema.optional(),
  consequence_of_failure: z.number().optional(),
  zone_recommendations: z.array(z.string()).optional().default([]),
  assumptions: z.array(z.string()).optional().default([]),
  review_notes: z.string().optional(),
})

// Zone Analysis Config Schema
export const zoneAnalysisConfigSchema = z.object({
  zoneFieldName: z.string().default(ZONE_FIELD_IDS.ZONES),
  confidenceFieldName: z.string().optional(), // Field name for confidence from NII Screening
  // Workflow-defined sections (the ZoneAnalysis step carries a section whose
  // `agent` block drives the backend zone-suggestion strategy). The frontend
  // only needs each section's `agentPermissions` — the per-section AI gating
  // every other agent step already honors — so the rest is ignored.
  sections: z
    .array(
      z.object({
        id: z.string(),
        agentPermissions: z.array(z.string()).optional(),
      })
    )
    .optional(),
})

export const zoneAnalysisDataSchema = z.object({
  zones: z.array(zoneParamsSchema).optional().default([]),
  analysisDate: z.string().default(now),
})

export type ConsequenceOfFailure = z.infer<typeof consequenceOfFailureSchema>
export type DamageMechanism = z.infer<typeof damageMechanismSchema>
export type NiiStrategy = z.infer<typeof niiStrategySchema>
export type RbaFactor = z.infer<typeof rbaFactorSchema>
export type RbaInformation = z.infer<typeof rbaInformationSchema>
export type CoverageCalculation = z.infer<typeof coverageCalculationSchema>
export type ZoneParams = z.infer<typeof zoneParamsSchema>
export type ZoneAnalysisConfig = z.infer<typeof zoneAnalysisConfigSchema>
export type ZoneAnalysisData = z.infer<typeof zoneAnalysisDataSchema>

// ============================================================================
// HOIS-RP-103 Lookup Tables and Modifiers
// ============================================================================

/**
 * F_CONS - Consequence of Failure Modifier (same for Type A and B)
 */
export function getCofModifier(level: TriState): number {
  const modifiers: Record<TriState, number> = {
    low: 0.6,
    medium: 0.8,
    high: 1.0,
  }
  return modifiers[level]
}

/**
 * F_COV - Coverage Modifier (Confidence × Density lookup)
 * Source: Table 4-9 (Type A), Table 4-11 (Type B) - same values for both
 */
export function getFCov(
  confidence: 'low' | 'med' | 'high',
  density: TriState
): number {
  const table: Record<'low' | 'med' | 'high', Record<TriState, number>> = {
    low: { high: 1.25, medium: 2.0, low: 4.0 },
    med: { high: 0.75, medium: 1.0, low: 2.0 },
    high: { high: 0.5, medium: 0.75, low: 1.25 },
  }
  return table[confidence][density]
}

/**
 * F_ZONE - Zone Area Modifier
 * Source: Table 4-10 (Type A), Table 4-16 (Type B) - same values for both
 */
export function getFZone(areaM2: number): number {
  if (areaM2 < 5) return 2.0
  if (areaM2 < 10) return 1.5
  if (areaM2 < 50) return 1.0
  if (areaM2 < 100) return 0.75
  return 0.5
}

/**
 * F_ACC - Measurement Accuracy Modifier (Type B only)
 * Source: Table 4-12
 */
export function getFAcc(accuracy: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 1.0,
    medium: 1.15,
    low: 1.25,
  }
  return modifiers[accuracy]
}

/**
 * F_LC - Tolerance to Degradation Modifier (Type B only)
 * Source: Table 4-13
 */
export function getFLc(tolerance: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 0.8,
    medium: 1.0,
    low: 1.2,
  }
  return modifiers[tolerance]
}

/**
 * F_SH - Spatial Homogeneity Modifier (Type B only)
 * Source: Table 4-15
 */
export function getFSh(homogeneity: TriState): number {
  const modifiers: Record<TriState, number> = {
    high: 1.0,
    medium: 1.15,
    low: 1.25,
  }
  return modifiers[homogeneity]
}

/**
 * Type B Floor and Ceiling based on Confidence
 * Source: Table 4-17
 */
export function getTypeBLimits(confidence: 'low' | 'med' | 'high'): {
  min: number
  max: number
} {
  const limits: Record<'low' | 'med' | 'high', { min: number; max: number }> = {
    high: { min: 7.5, max: 40 },
    med: { min: 12.5, max: 50 },
    low: { min: 20, max: 80 },
  }
  return limits[confidence]
}

export function getBaseCoverage(strategyType: InspectionStrategy): number {
  const baseCoverages: Record<InspectionStrategy, number> = {
    A: 7.5,
    B: 25,
    C: 100,
  }
  return baseCoverages[strategyType]
}

export function getEffectiveStrategyType(
  strategy?: NiiStrategy
): InspectionStrategy | undefined {
  if (!strategy) return undefined
  return strategy.strategy_type_override ?? strategy.strategy_type_calculated
}

export function getEffectiveZoneArea(zone: ZoneParams): number {
  return (
    zone.total_surface_area_sq_m_override ??
    zone.total_surface_area_sq_m ??
    zone.calculated_area_sq_m ??
    0
  )
}

export function getEffectiveZoneLength(zone: ZoneParams): number | undefined {
  return zone.length_mm_override ?? zone.length_mm ?? zone.calculated_length_mm
}

export function getEffectiveZoneHeight(zone: ZoneParams): number | undefined {
  return zone.height_mm_override ?? zone.height_mm ?? zone.calculated_height_mm
}

/**
 * Calculate required coverage per HOIS-RP-103
 * @param zone - The zone being analyzed
 * @param globalCofModifier - Global CoF modifier (from ConsequenceOfFailure section)
 * @param confidence - Confidence from NII Screening ('low' | 'med' | 'high')
 */
export function calculateRequiredCoverage(
  zone: ZoneParams,
  globalCofModifier: number,
  confidence: 'low' | 'med' | 'high'
): CoverageCalculation | undefined {
  const strategy = zone.nii_strategy
  const rba = zone.rba_info

  if (!strategy) return undefined

  const effectiveStrategyType = getEffectiveStrategyType(strategy)
  if (!effectiveStrategyType) return undefined

  const baseCoverage = getBaseCoverage(effectiveStrategyType)
  const totalArea = getEffectiveZoneArea(zone)

  // Type C: Always 100%
  if (effectiveStrategyType === InspectionStrategy.C) {
    return {
      base_coverage_percent: 100,
      required_coverage_percent: 100,
      required_coverage_area_sq_m: totalArea,
      f_cons: globalCofModifier,
    }
  }

  // Get shared modifiers for Type A and B
  const density = rba?.density_of_degradation ?? 'medium'
  const fCov = getFCov(confidence, density)
  const fCons = globalCofModifier
  const fZone = getFZone(totalArea)

  // Type A: C_R = max(2.5%, F_COV × F_CONS × F_ZONE × 7.5%)
  if (effectiveStrategyType === InspectionStrategy.A) {
    const calculated = fCov * fCons * fZone * baseCoverage
    const requiredCoveragePercent = Math.max(2.5, calculated)
    const requiredCoverageArea = totalArea * (requiredCoveragePercent / 100)

    return {
      base_coverage_percent: baseCoverage,
      f_cov: fCov,
      f_cons: fCons,
      f_zone: fZone,
      raw_calculated_percent: calculated,
      required_coverage_percent: requiredCoveragePercent,
      required_coverage_area_sq_m: requiredCoverageArea,
    }
  }

  // Type B: C_R = clamp(C_min, C_max, F_COV × F_ACC × F_LC × F_CONS × F_SH × F_ZONE × 25%)
  if (effectiveStrategyType === InspectionStrategy.B) {
    const accuracy = rba?.measurement_accuracy ?? 'medium'
    const tolerance = rba?.tolerance_of_degradation ?? 'medium'
    const homogeneity = rba?.spatial_homogeneity ?? 'medium'

    const fAcc = getFAcc(accuracy)
    const fLc = getFLc(tolerance)
    const fSh = getFSh(homogeneity)

    const calculated = fCov * fAcc * fLc * fCons * fSh * fZone * baseCoverage
    const { min, max } = getTypeBLimits(confidence)
    const requiredCoveragePercent = Math.min(max, Math.max(min, calculated))
    const requiredCoverageArea = totalArea * (requiredCoveragePercent / 100)

    return {
      base_coverage_percent: baseCoverage,
      f_cov: fCov,
      f_acc: fAcc,
      f_lc: fLc,
      f_cons: fCons,
      f_sh: fSh,
      f_zone: fZone,
      raw_calculated_percent: calculated,
      required_coverage_percent: requiredCoveragePercent,
      required_coverage_area_sq_m: requiredCoverageArea,
    }
  }

  return undefined
}

export function useSaveZoneData(
  fieldName: string = ZONE_FIELD_IDS.ZONES
): (zones: ZoneParams[]) => Promise<void> {
  const { setFieldValues } = useTicketAccess()
  return useCallback(
    async (zones: ZoneParams[]) => {
      await setFieldValues({ [fieldName]: wrapFieldValue(zones) })
    },
    [setFieldValues, fieldName]
  )
}

// ============================================================================
// Pure field-value parsers (shared by hooks and non-React callers)
// ============================================================================

const DEFAULT_COF: ConsequenceOfFailure = {
  level: 'medium' as const,
  justification: undefined,
}

const DEFAULT_SCREENING_CONFIDENCE = 'med' as const

const zoneArraySchema = z.array(zoneParamsSchema)

/**
 * Pure parser for the zones field on a ticket. Returns the parsed zones plus
 * an optional `parseError` so callers can decide how to surface failures
 * (e.g. an `errorNotification` in `useZoneData`, or just the console log
 * elsewhere). Returns `[]` when the field is absent.
 */
export function parseZonesFromFieldValues(
  fieldValues: FieldValues,
  fieldName: string = ZONE_FIELD_IDS.ZONES
): { zones: ZoneParams[]; parseError?: z.ZodError } {
  const rawZones = unwrapFieldValue<ZoneParams[]>(fieldValues[fieldName])
  if (!rawZones) return { zones: [] }
  const parsed = zoneArraySchema.safeParse(rawZones)
  if (!parsed.success) {
    console.error('Zone parse error:', parsed.error)
    return { zones: [], parseError: parsed.error }
  }
  return { zones: parsed.data }
}

/**
 * Pure parser for the Consequence-of-Failure field. Defaults to
 * `{ level: 'medium' }` when missing or invalid.
 */
export function parseCofFromFieldValues(fieldValues: FieldValues): {
  cof: ConsequenceOfFailure
  parseError?: z.ZodError
} {
  const rawCof = unwrapFieldValue<ConsequenceOfFailure>(
    fieldValues['consequence_of_failure']
  )
  if (!rawCof) return { cof: DEFAULT_COF }
  const parsed = consequenceOfFailureSchema.safeParse(rawCof)
  if (!parsed.success) {
    console.error('CoF parse error:', parsed.error)
    return { cof: DEFAULT_COF, parseError: parsed.error }
  }
  return { cof: parsed.data }
}

/**
 * Pure parser for the NII Screening confidence field. Defaults to `'med'`
 * when no `confidenceFieldName` is configured or the field is missing.
 */
export function parseConfidenceFromFieldValues(
  fieldValues: FieldValues,
  confidenceFieldName?: string
): 'low' | 'med' | 'high' {
  if (!confidenceFieldName) return DEFAULT_SCREENING_CONFIDENCE
  const rawValue = unwrapFieldValue<'low' | 'med' | 'high'>(
    fieldValues[confidenceFieldName]
  )
  return rawValue ?? DEFAULT_SCREENING_CONFIDENCE
}

export function useZoneData(fieldName: string = ZONE_FIELD_IDS.ZONES): {
  isLoading: boolean
  zones: ZoneParams[]
} {
  const { fieldValues, isLoading } = useTicketAccess()
  const { errorNotification } = useGeckoNotifications()

  return useMemo(() => {
    const { zones, parseError } = parseZonesFromFieldValues(
      fieldValues,
      fieldName
    )
    if (parseError) {
      errorNotification(`Invalid zone data in field: ${fieldName}`)
    }
    return { isLoading, zones }
  }, [fieldValues, fieldName, isLoading, errorNotification])
}

export function useCofData(): {
  isLoading: boolean
  cof: ConsequenceOfFailure
} {
  const { fieldValues, isLoading } = useTicketAccess()
  const { errorNotification } = useGeckoNotifications()

  return useMemo(() => {
    const { cof, parseError } = parseCofFromFieldValues(fieldValues)
    if (parseError) {
      errorNotification('Invalid consequence of failure data')
    }
    return { isLoading, cof }
  }, [fieldValues, isLoading, errorNotification])
}

export function useSaveCofData(): (cof: ConsequenceOfFailure) => Promise<void> {
  const { setFieldValues } = useTicketAccess()
  return useCallback(
    async (cof: ConsequenceOfFailure) => {
      await setFieldValues({
        consequence_of_failure: wrapFieldValue(cof),
      })
    },
    [setFieldValues]
  )
}

export function useZoneBreakdownJustificationData(): {
  isLoading: boolean
  zoneBreakdownJustification: string | undefined
} {
  const { fieldValues, isLoading } = useTicketAccess()
  return useMemo(() => {
    const raw = unwrapFieldValue<string>(
      fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]
    )
    return { isLoading, zoneBreakdownJustification: raw ?? undefined }
  }, [fieldValues, isLoading])
}

export function useSaveZoneBreakdownJustification(): (
  justification: string | undefined
) => Promise<void> {
  const { setFieldValues } = useTicketAccess()
  return useCallback(
    async (justification: string | undefined) => {
      await setFieldValues({
        zone_breakdown_justification: wrapFieldValue(justification ?? ''),
      })
    },
    [setFieldValues]
  )
}

/**
 * Hook to read confidence value from NII Screening step
 * Uses the confidenceFieldName from config to determine which field to read
 */
export function useConfidenceFromScreening(
  confidenceFieldName?: string
): 'low' | 'med' | 'high' {
  const { fieldValues } = useTicketAccess()
  return useMemo(
    () => parseConfidenceFromFieldValues(fieldValues, confidenceFieldName),
    [fieldValues, confidenceFieldName]
  )
}
